import * as QueryKeys from "data";
import { fetchAdminBounds, fetchPredictiveLocality } from "data/queries-common";
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalityResult } from "./models";
import { PredictiveSuburbManager } from "./predictive-suburb-manager";

export const SuburbEntryForm = (predictiveManager: PredictiveSuburbManager) => {
  const [inputValue, setInputValue] = useState("");
  const [localityResult, setLocalityResult] = useState<LocalityResult | null>(
    null,
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = "locality-suggestions";

  const debouncedSearch = useCallback(
    _.debounce((searchTerm: string) => {
      predictiveManager.setQuery(searchTerm);
    }, predictiveManager.debounce),
    [predictiveManager.debounce],
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowSuggestions(true);
    setHighlightedIndex(-1);
    debouncedSearch(value);
  };

  const handleSelect = (option: LocalityResult) => {
    predictiveManager.setSuggest(null);
    setLocalityResult(option);
    setInputValue(option.displayName);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const predictiveQuery = useQuery({
    queryKey: [
      QueryKeys.predictiveLocality,
      predictiveManager.query,
      predictiveManager.postcode,
      predictiveManager.stateFilter.join(","),
      predictiveManager.maxResults,
    ],
    queryFn: async () => {
      const result = await fetchPredictiveLocality(
        predictiveManager.query,
        predictiveManager.postcode,
        predictiveManager.stateFilter.join(","),
        predictiveManager.maxResults,
      );
      predictiveManager.setCallCounter((c) => c + 1);
      predictiveManager.setCreditCounter((c) => c + result.basictransaction);
      return result;
    },
    enabled: !!predictiveManager.query,
  });

  useEffect(() => {
    if (predictiveQuery.data) predictiveManager.setSuggest(predictiveQuery.data);
  }, [predictiveQuery.data]);

  const adminBoundsByIdQuery = useQuery({
    queryKey: [
      QueryKeys.adminBoundsById,
      localityResult?.id,
      predictiveManager.crs,
    ],
    queryFn: async () => {
      const result = await fetchAdminBounds(
        localityResult?.id ?? "",
        "localities",
        false,
        predictiveManager.crs,
      );
      predictiveManager.setCallCounter((c) => c + 1);
      predictiveManager.setCreditCounter((c) => c + result.basictransaction);
      return result;
    },
    enabled: !!localityResult?.id,
  });

  useEffect(() => {
    if (adminBoundsByIdQuery.data)
      predictiveManager.setLocality(adminBoundsByIdQuery.data);
  }, [adminBoundsByIdQuery.data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${_.escapeRegExp(query)})`, "gi"));
    return parts.map((part, index) => (
      <span
        key={index}
        className={
          part.toLowerCase() === query.toLowerCase() ? "font-bold" : ""
        }
      >
        {part}
      </span>
    ));
  };

  const suggestions = predictiveQuery.data?.suggest ?? [];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1,
        );
        break;
      case "Enter":
        if (highlightedIndex >= 0) {
          e.preventDefault();
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <Card className="overflow-visible">
      <CardHeader>
        <CardTitle>Locality/Suburb Search Form</CardTitle>
        <CardDescription>
          Search for Localities by name or by postcode
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div ref={containerRef} className="relative space-y-1.5">
          <Input
            id="locality-search"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => inputValue && setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder="Start typing to get auto-complete suggestions from the API"
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-controls={listboxId}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${listboxId}-option-${highlightedIndex}`
                : undefined
            }
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
              <ul
                id={listboxId}
                role="listbox"
                className="max-h-60 overflow-y-auto py-1"
              >
                {suggestions.map((option, index) => (
                  <li
                    key={option.id}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    className={`cursor-pointer px-3 py-1.5 text-sm hover:bg-accent ${
                      index === highlightedIndex ? "bg-accent" : ""
                    }`}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => handleSelect(option)}
                  >
                    {highlightText(option.displayName, inputValue)}
                  </li>
                ))}
              </ul>
              <div className="flex justify-end gap-1 px-2 py-1 border-t text-xs text-muted-foreground">
                <span>Powered by</span>
                <span className="font-bold">Geoscape</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="locality-state" className="text-xs">
              State/Territory
            </Label>
            <Input
              id="locality-state"
              value={localityResult?.stateTerritory ?? ""}
              disabled={!localityResult}
              readOnly
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="locality-postcode" className="text-xs">
              Postcode
            </Label>
            <Input
              id="locality-postcode"
              value={localityResult?.postcode ?? ""}
              disabled={!localityResult}
              readOnly
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
